import { Controller } from "@hotwired/stimulus"
import flash from "../lib/flashes.js"

export default class extends Controller {
  static values = {
    actions: Array, // Array of action objects: {label, action, icon, url, method, confirm}
    companyId: String
  }

  static targets = ["menu", "counter"]

  connect() {
    this.hide()
    this.renderActions()
  }

  // Listen for bulk selector events
  handleSelectionChanged(event) {
    const { hasSelection, count } = event.detail
    
    if (hasSelection) {
      this.updateCounter(count)
      this.show()
    } else {
      this.hide()
    }
  }

  show() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.remove('floating-menu--hidden')
      this.menuTarget.classList.add('floating-menu--visible')
    }
  }

  hide() {
    if (this.hasMenuTarget) {
      this.menuTarget.classList.remove('floating-menu--visible')
      this.menuTarget.classList.add('floating-menu--hidden')
    }
  }

  updateCounter(count) {
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${count} selected`
    }
  }

  renderActions() {
    if (!this.hasMenuTarget || !this.actionsValue.length) return

    const actionsContainer = this.menuTarget.querySelector('.floating-menu__actions')
    if (!actionsContainer) return

    actionsContainer.innerHTML = ''

    this.actionsValue.forEach(action => {
      const button = document.createElement('button')
      button.className = `floating-menu__action ${action.danger ? 'floating-menu__action--danger' : ''}`
      button.dataset.action = `click->floating-action-menu#${action.action}`
      button.dataset.actionConfig = JSON.stringify(action)
      
      if (action.icon) {
        button.innerHTML = `<i class="${action.icon}"></i>`
      } else {
        button.textContent = action.label
      }
      
      if (action.label) {
        button.setAttribute('data-tippy-content', action.label)
      }

      actionsContainer.appendChild(button)
    })
  }

  move() {
    const bulkSelectorController = this.application.getControllerForElementAndIdentifier(this.element, 'bulk-selector')
    if (!bulkSelectorController || !bulkSelectorController.selectedIdsValue.length) return

    // Build the modal URL using stored values
    const url = new URL('/moves/new', window.location.origin)
    const entity = bulkSelectorController.entityValue
    const capitalizedEntity = entity.charAt(0).toUpperCase() + entity.slice(1)
    url.searchParams.set('move_type', `${capitalizedEntity}Folder`)
    url.searchParams.set('record_type', capitalizedEntity)
    url.searchParams.set('ids', JSON.stringify(bulkSelectorController.selectedIdsValue))
    url.searchParams.set('single_move', 'true')
    
    if (this.companyIdValue) {
      url.searchParams.set('company_id', this.companyIdValue)
    }

    Turbo.visit(url.toString(), { frame: 'modal' })
  }

  delete(event) {
    this.handleAction(event, 'delete')
  }

  close() {
    // Clear the selection when closing
    // The bulk-selector controller is on the same element as floating-action-menu
    const bulkSelectorController = this.application.getControllerForElementAndIdentifier(this.element, 'bulk-selector')
    if (bulkSelectorController) {
      bulkSelectorController.clearSelection()
    }
    
    this.dispatch("close-requested")
    this.hide()
  }

  handleAction(event, actionType) {
    const button = event.target.closest('button')
    const actionConfig = JSON.parse(button.dataset.actionConfig)
    
    // Get selected IDs from bulk selector (same element)
    const bulkSelectorController = this.application.getControllerForElementAndIdentifier(this.element, 'bulk-selector')
    const selectedIds = bulkSelectorController ? bulkSelectorController.selectedIds : []

    if (!selectedIds.length) return

    // Handle confirmation if required
    if (actionConfig.confirm) {
      const confirmMessage = actionConfig.confirm.replace('{count}', selectedIds.length)
      if (!confirm(confirmMessage)) return
    }
    
    // If URL is provided, make the API call
    if (actionConfig.url) {
      this.makeApiCall(actionConfig, selectedIds)
    }
  }

  async makeApiCall(actionConfig, selectedIds) {
    try {
      const formData = new FormData()
      formData.append('ids', JSON.stringify(selectedIds))
      formData.append('action', actionConfig.action)

      const response = await fetch(actionConfig.url, {
        method: actionConfig.method || 'POST',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })
      
      this.close()
      Turbo.visit(window.location.href, { action: "replace" })
    } catch (error) {
      flash(`Failed to update the photo(s): ${error.message}`, "error");
    }
  }
} 